import { useState, useEffect, useCallback, useRef } from 'react';
import { simulationService } from '../services/simulation.service';
import { resolveEffectiveVoiceFields, VoiceFields } from '../services/voice-contract';
import { Turn, SimulationState, ScenarioBriefingData } from '../types';

import { isFemaleName } from '../../../services/voiceService';

export interface UseSimulationLifecycleOptions {
  scenarioId?: string;
  attemptId?: string;
  language?: string;
  enabled?: boolean;
  initialAttemptData?: any;
  onInitialTurn?: (turn: Turn) => void;
  onAiResponse?: (turn: Turn) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

/**
 * Legacy voice resolution. The backend now resolves the attempt's voice, engine and
 * gender at startup, so this runs only when a response carries no contract (older
 * server, cached payload); see `services/voice-contract.ts`.
 */
function resolvePersonaVoice(pName?: string, pId?: string, sourceConfig?: any) {
  const personas =
    sourceConfig?.personas ||
    sourceConfig?.simulation?.personas ||
    sourceConfig?.scenarioConfig?.personas ||
    sourceConfig?.scenario?.personas ||
    [];
  const found =
    personas.find(
      (p: any) =>
        (pId && p.id === pId) ||
        (pName && p.name && p.name.split('(')[0].trim().toLowerCase() === pName.split('(')[0].trim().toLowerCase())
    ) ||
    personas[0] ||
    sourceConfig?.persona ||
    {};

  const cleanName = pName || found.name || '';
  const detectedGender = isFemaleName(cleanName) ? 'female' : 'male';
  const effectiveGender = found.genderPreference || found.voiceConfig?.genderPreference || sourceConfig?.genderPreference || detectedGender;

  return {
    ttsVoice: found.ttsVoice || sourceConfig?.ttsVoice || (effectiveGender === 'male' ? 'onyx' : 'nova'),
    ttsProvider: found.ttsProvider || sourceConfig?.effectiveTTSProvider || sourceConfig?.ttsProvider || 'browser',
    country: found.country || sourceConfig?.country || 'IN',
    tone: found.tone || sourceConfig?.tone || 'Empathetic',
    speakingPace:
      typeof found.speakingPace === 'number'
        ? found.speakingPace
        : typeof found.voiceConfig?.speakingRate === 'number'
        ? found.voiceConfig.speakingRate
        : undefined,
    pitchPreference:
      typeof found.pitchPreference === 'number'
        ? found.pitchPreference
        : typeof found.voiceConfig?.pitch === 'number'
        ? found.voiceConfig.pitch
        : undefined,
    genderPreference: effectiveGender,
  };
}

// Bounded timeout for attempt start / resume (25000ms cloud tolerance; 9000ms baseline)
const ATTEMPT_INIT_TIMEOUT_MS = 25000;

function withTimeout<T>(promise: Promise<T>, timeoutMs = 9000, errorMsg = 'Operation timed out.'): Promise<T> {
  let timer: NodeJS.Timeout | null = null;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(errorMsg));
    }, timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

export function useSimulationLifecycle({
  scenarioId,
  attemptId: initialAttemptId,
  language = 'en',
  enabled = true,
  initialAttemptData,
  onInitialTurn,
  onAiResponse,
  onError,
  onComplete,
}: UseSimulationLifecycleOptions) {
  const [attemptId, setAttemptId] = useState<string | undefined>(initialAttemptId);
  const [state, _setState] = useState<SimulationState>(enabled ? 'initializing' : 'ready');
  // Always keep stateRef in sync so async callbacks read the live value
  const setState = useCallback((nextState: SimulationState) => {
    stateRef.current = nextState;
    _setState(nextState);
  }, []);
  const [briefing, setBriefing] = useState<ScenarioBriefingData | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [videoAnalysisEnabled, setVideoAnalysisEnabled] = useState<boolean>(false);
  const [videoAnalysisConfig, setVideoAnalysisConfig] = useState<any>(undefined);
  const [effectiveTTSProvider, setEffectiveTTSProvider] = useState<string>('google');
  const [currentStageIndex, setCurrentStageIndex] = useState<number>(0);
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [initProgressStage, setInitProgressStage] = useState<'connecting' | 'starting' | 'ready' | 'error'>('connecting');
  const activeCancelRef = useRef<{ isCancelled: boolean }>({ isCancelled: false });
  // Voice contract locked by attempt startup, replayed on every later turn so the
  // persona cannot switch voice or engine just because a turn response omits a field.
  const voiceContractRef = useRef<VoiceFields | null>(null);
  // Mirrored as state so the room re-renders with the authoritative engine instead
  // of falling through to a locally guessed (browser) provider.
  const [voiceContract, setVoiceContract] = useState<VoiceFields | null>(null);
  const lockVoiceContract = useCallback((fields: VoiceFields | null) => {
    voiceContractRef.current = fields;
    setVoiceContract(fields);
  }, []);
  const isSubmittingTurnRef = useRef(false);
  const isCompletingRef = useRef(false);
  const initializedAttemptRef = useRef<string | null>(null);
  const isInitializingRef = useRef(false);
  const stateRef = useRef<SimulationState>(enabled ? 'initializing' : 'ready');

  // A new attempt must be completable even if the previous one already finalized
  useEffect(() => {
    isCompletingRef.current = false;
  }, [attemptId]);

  const onInitialTurnRef = useRef(onInitialTurn);
  onInitialTurnRef.current = onInitialTurn;
  const onAiResponseRef = useRef(onAiResponse);
  onAiResponseRef.current = onAiResponse;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Initialize or resume attempt
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const sessionKey = initialAttemptId ? `attempt_${initialAttemptId}` : `scenario_${scenarioId}_${language}`;
    if (initializedAttemptRef.current === sessionKey || isInitializingRef.current) {
      return;
    }
    isInitializingRef.current = true;

    async function initSession(cancelToken: { isCancelled: boolean }) {
      if (!scenarioId && !initialAttemptId) {
        setState('error');
        setError('No scenarioId or attemptId provided.');
        isInitializingRef.current = false;
        return;
      }

      setState('initializing');
      setInitProgressStage('connecting');
      try {
        if (initialAttemptId) {
          // Pre-fetch scenario briefing in background parallel to attempt resume
          const briefingPromise = scenarioId
            ? simulationService.getBriefing(scenarioId).catch(() => null)
            : Promise.resolve(null);

          // Fast-path: Check for preloaded attempt data passed from briefing or stored in sessionStorage
          let preloadedPayload = initialAttemptData || null;
          if (!preloadedPayload && typeof window !== 'undefined') {
            try {
              const raw = sessionStorage.getItem(`trainova_attempt_launch_${initialAttemptId}`);
              if (raw) {
                preloadedPayload = JSON.parse(raw);
                sessionStorage.removeItem(`trainova_attempt_launch_${initialAttemptId}`);
              }
            } catch {}
          }

          let res: any;
          if (preloadedPayload && ((preloadedPayload as any).id === initialAttemptId || (preloadedPayload as any).attemptId === initialAttemptId)) {
            res = { success: true, data: preloadedPayload };
          } else {
            // Resume existing attempt with bounded timeout (25000ms cloud tolerance; 9000ms baseline)
            res = await withTimeout(
              simulationService.resumeAttempt(initialAttemptId),
              ATTEMPT_INIT_TIMEOUT_MS,
              'Attempt resume connection timed out.'
            );
          }
          if (cancelToken.isCancelled) return;

          if (res.success && res.data) {
            setAttemptId(initialAttemptId);

            if ((res.data as any).videoAnalysisEnabled !== undefined) {
              setVideoAnalysisEnabled(Boolean((res.data as any).videoAnalysisEnabled));
            } else if ((res.data as any).attemptSnapshot?.videoAnalysisEnabled !== undefined) {
              setVideoAnalysisEnabled(Boolean((res.data as any).attemptSnapshot?.videoAnalysisEnabled));
            }
            if ((res.data as any).videoAnalysisConfig) {
              setVideoAnalysisConfig((res.data as any).videoAnalysisConfig);
            } else if ((res.data as any).attemptSnapshot?.videoAnalysisConfig) {
              setVideoAnalysisConfig((res.data as any).attemptSnapshot?.videoAnalysisConfig);
            }
            if ((res.data as any).effectiveTTSProvider) {
              setEffectiveTTSProvider((res.data as any).effectiveTTSProvider);
            } else if ((res.data as any).attemptSnapshot?.effectiveTTSProvider) {
              setEffectiveTTSProvider((res.data as any).attemptSnapshot?.effectiveTTSProvider);
            }

            // The attempt already locked its voice contract at startup; replay it.
            const resumedVoice = resolveEffectiveVoiceFields(
              [[res.data, (res.data as any).attemptSnapshot]],
              {}
            );
            if (resumedVoice.isBackendAuthoritative) {
              lockVoiceContract(resumedVoice);
            }

            const rawTurns =
              res.data.turns ||
              (res.data as any).state?.autosaveData?.turns ||
              (res.data as any).attemptSnapshot?.scenario?.turns ||
              [];

            // Turns saved before the voice contract exist carry no voice fields at
            // all. Re-attaching the contract here is what keeps the opening line on the
            // same engine as every later reply instead of the device's own synthesizer.
            const resumeFallback = resolvePersonaVoice(
              (res.data as any).personaName ||
                (res.data.scenarioConfig as any)?.personas?.[0]?.name ||
                (res.data as any).attemptSnapshot?.scenario?.personas?.[0]?.name,
              undefined,
              res.data.scenarioConfig || (res.data as any).attemptSnapshot?.scenario || briefing
            );

            let normalizedTurns: Turn[] = (Array.isArray(rawTurns) ? rawTurns : []).map((t: any) => {
              const speakerName = (
                (t.speaker || t.role || '').toUpperCase() === 'LEARNER' ||
                (t.speaker || t.role || '').toUpperCase() === 'USER'
                  ? 'LEARNER'
                  : (t.speaker || t.role || '').toUpperCase() === 'COACH'
                  ? 'COACH'
                  : 'PERSONA'
              ) as any;
              const isPersonaTurn = speakerName === 'PERSONA' || speakerName === 'COACH';
              const turnVoice = isPersonaTurn
                ? resolveEffectiveVoiceFields([[t], [voiceContractRef.current]], resumeFallback)
                : null;
              return {
                speaker: speakerName,
                personaName: t.personaName,
                speakerName: t.speakerName,
                text: t.text || t.content || t.message || '',
                timestamp: typeof t.timestamp === 'number' ? t.timestamp : (t.timestamp ? new Date(t.timestamp).getTime() : Date.now()),
                audioUrl: t.audioUrl || (t.audioBase64 ? `data:${t.audioMimeType || 'audio/mpeg'};base64,${t.audioBase64}` : undefined),
                ttsVoice: turnVoice?.ttsVoice ?? t.ttsVoice,
                ttsProvider: (turnVoice?.ttsProvider ?? t.ttsProvider) as Turn['ttsProvider'],
                genderPreference: turnVoice?.genderPreference ?? t.genderPreference,
                country: turnVoice?.country ?? t.country,
                tone: t.tone,
                speakingPace: t.speakingPace,
                pitchPreference: t.pitchPreference,
                stageIndex: typeof t.stageIndex === 'number' && !isNaN(t.stageIndex) ? t.stageIndex : (typeof t.dialogueState?.stageIndex === 'number' && !isNaN(t.dialogueState.stageIndex) ? t.dialogueState.stageIndex : undefined),
                stageName: t.stageName || t.dialogueState?.stageName,
                llmDebug: t.llmDebug,
                latencyMetrics: t.latencyMetrics,
              };
            });

            const openingAudio = (res.data as any).openingAudioBase64
              ? `data:${(res.data as any).openingMimeType || 'audio/mpeg'};base64,${(res.data as any).openingAudioBase64}`
              : ((res.data as any).openingAudioUrl || undefined);
            const opening =
              (res.data as any).openingUtterance ||
              (res.data as any).openingStatement ||
              (res.data.scenarioConfig as any)?.openingStatement;

            if (
              normalizedTurns.length === 0 &&
              opening
            ) {
              const initialPersonaName =
                (res.data as any).personaName ||
                (res.data.scenarioConfig as any)?.personas?.[0]?.name ||
                (res.data as any).attemptSnapshot?.scenario?.personas?.[0]?.name ||
                'Customer';
              const localVoice = resolvePersonaVoice(
                initialPersonaName,
                undefined,
                res.data.scenarioConfig || (res.data as any).attemptSnapshot?.scenario || briefing
              );
              // Backend-resolved values win; local resolution only covers legacy responses.
              const voiceConfig = voiceContractRef.current || localVoice;
              normalizedTurns = [
                {
                  speaker: 'PERSONA',
                  personaName: initialPersonaName,
                  speakerName: initialPersonaName,
                  text: opening,
                  timestamp: Date.now(),
                  audioUrl: openingAudio,
                  ttsVoice: voiceConfig.ttsVoice,
                  ttsProvider: voiceConfig.ttsProvider as Turn['ttsProvider'],
                  country: voiceConfig.country || localVoice.country,
                  tone: localVoice.tone,
                  speakingPace: localVoice.speakingPace,
                  pitchPreference: localVoice.pitchPreference,
                  genderPreference: voiceConfig.genderPreference || localVoice.genderPreference,
                },
              ];
            } else if (
              normalizedTurns.length > 0 &&
              (normalizedTurns[0].speaker === 'PERSONA' || (normalizedTurns[0].speaker as any) === 'AI')
            ) {
              if (!normalizedTurns[0].audioUrl && openingAudio) {
                normalizedTurns[0].audioUrl = openingAudio;
              }
              if (!normalizedTurns[0].text && opening) {
                normalizedTurns[0].text = opening;
              }
            }

            setTurns(normalizedTurns);
            const resumedStageTurn = [...normalizedTurns].reverse().find((t: any) => typeof t.stageIndex === 'number' && !isNaN(t.stageIndex));
            const resumedStageIdx = resumedStageTurn?.stageIndex ?? (res.data as any).currentStageIndex ?? (res.data as any).attemptSnapshot?.currentStageIndex;
            if (typeof resumedStageIdx === 'number' && !isNaN(resumedStageIdx)) {
              setCurrentStageIndex(resumedStageIdx);
            }
            const scenarioConfig = res.data.scenarioConfig || res.data.scenario || (res.data as any).attemptSnapshot?.scenario;
            if (scenarioConfig) {
              // Carry the server-derived clock so a resumed attempt does not restart
              // its countdown; the briefing merge below preserves these fields.
              setBriefing({
                ...(scenarioConfig as any),
                startedAt: (res.data as any).startedAt || (scenarioConfig as any)?.startedAt,
                timeLimitMinutes: (res.data as any).timeLimitMinutes || (scenarioConfig as any)?.timeLimitMinutes,
                elapsedSeconds: (res.data as any).elapsedSeconds ?? (scenarioConfig as any)?.elapsedSeconds,
                timeRemainingSeconds: (res.data as any).timeRemainingSeconds ?? (scenarioConfig as any)?.timeRemainingSeconds,
              } as any);
            }
            // Asynchronously enrich briefing with extra persona metadata without blocking room startup
            void briefingPromise.then((bRes) => {
              if (cancelToken.isCancelled || !bRes?.success || !bRes?.data) return;
              const bData = bRes.data;
              const simObj = bData.simulation || bData;
              const mergedPersonas = simObj.personas || bData.personas || scenarioConfig?.personas || [];

              setBriefing((prev: any) => ({
                ...prev,
                ...bData,
                simulation: {
                  ...(prev?.simulation || prev || {}),
                  ...simObj,
                  personas: mergedPersonas.length > 0 ? mergedPersonas : prev?.personas,
                },
                personas: mergedPersonas.length > 0 ? mergedPersonas : prev?.personas,
              }));
            });

            initializedAttemptRef.current = sessionKey;
            setInitProgressStage('ready');
            setState('ready');
            if (normalizedTurns.length === 1 && normalizedTurns[0].speaker === 'PERSONA' && onInitialTurnRef.current) {
              onInitialTurnRef.current(normalizedTurns[0]);
            }
          } else {
            // Graceful fallback: If resuming specific attempt ID is not possible, start fresh for scenarioId
            if (scenarioId) {
              console.warn(`[useSimulationLifecycle] Resume failed for ${initialAttemptId}. Starting fresh attempt for ${scenarioId}.`);
              setState('starting');
              setInitProgressStage('starting');

              const [briefingRes, startRes] = await Promise.all([
                simulationService.getBriefing(scenarioId).catch(() => ({ success: false, data: null })),
                withTimeout(
                  simulationService.startAttempt(scenarioId, language),
                  ATTEMPT_INIT_TIMEOUT_MS,
                  'Dialogue engine start timed out.'
                )
              ]);
              if (cancelToken.isCancelled) return;

              if (briefingRes?.success && briefingRes?.data) {
                setBriefing(briefingRes.data);
              }
              if (startRes.success && startRes.data) {
                const newAttemptId = startRes.data.attemptId || (startRes.data as any).id;
                setAttemptId(newAttemptId);

                if ((startRes.data as any).videoAnalysisEnabled !== undefined) {
                  setVideoAnalysisEnabled(Boolean((startRes.data as any).videoAnalysisEnabled));
                } else if ((startRes.data as any).attemptSnapshot?.videoAnalysisEnabled !== undefined) {
                  setVideoAnalysisEnabled(Boolean((startRes.data as any).attemptSnapshot?.videoAnalysisEnabled));
                }
                if ((startRes.data as any).videoAnalysisConfig) {
                  setVideoAnalysisConfig((startRes.data as any).videoAnalysisConfig);
                } else if ((startRes.data as any).attemptSnapshot?.videoAnalysisConfig) {
                  setVideoAnalysisConfig((startRes.data as any).attemptSnapshot?.videoAnalysisConfig);
                }
                if ((startRes.data as any).effectiveTTSProvider) {
                  setEffectiveTTSProvider((startRes.data as any).effectiveTTSProvider);
                } else if ((startRes.data as any).attemptSnapshot?.effectiveTTSProvider) {
                  setEffectiveTTSProvider((startRes.data as any).attemptSnapshot?.effectiveTTSProvider);
                }

                // Attempt startup is the authority for voice, engine and gender.
                const startedVoice = resolveEffectiveVoiceFields(
                  [[(startRes.data as any).initialTurn, startRes.data, (startRes.data as any).attemptSnapshot]],
                  {}
                );
                if (startedVoice.isBackendAuthoritative) {
                  lockVoiceContract(startedVoice);
                }

                const initialText =
                  startRes.data.initialTurn?.text ||
                  (startRes.data as any).openingUtterance ||
                  (startRes.data as any).openingStatement;
                if (initialText) {
                  const audioUrl =
                    startRes.data.initialTurn?.audioUrl ||
                    ((startRes.data as any).openingAudioBase64
                      ? `data:${(startRes.data as any).openingMimeType || 'audio/mpeg'};base64,${(startRes.data as any).openingAudioBase64}`
                      : undefined);
                  const initialPersonaName =
                    (startRes.data as any)?.initialTurn?.personaName ||
                    (startRes.data as any)?.personaName ||
                    (startRes.data as any)?.scenarioConfig?.personas?.[0]?.name ||
                    (briefingRes?.data as any)?.personas?.[0]?.name ||
                    (briefingRes?.data as any)?.simulation?.personas?.[0]?.name ||
                    'Customer';
                  const localVoice = resolvePersonaVoice(
                    initialPersonaName,
                    undefined,
                    briefingRes?.data || (startRes.data as any)?.scenarioConfig || briefing
                  );
                  // Backend-resolved values win; local resolution only covers legacy responses.
                  const voiceConfig = voiceContractRef.current || localVoice;
                  const firstTurn: Turn = {
                    speaker: 'PERSONA',
                    personaName: initialPersonaName,
                    speakerName: initialPersonaName,
                    text: initialText,
                    timestamp: Date.now(),
                    audioUrl,
                    ttsVoice: voiceConfig.ttsVoice,
                    ttsProvider: voiceConfig.ttsProvider as Turn['ttsProvider'],
                    country: voiceConfig.country || localVoice.country,
                    tone: localVoice.tone,
                    speakingPace: localVoice.speakingPace,
                    pitchPreference: localVoice.pitchPreference,
                    genderPreference: voiceConfig.genderPreference || localVoice.genderPreference,
                  };
                  setTurns([firstTurn]);
                  initializedAttemptRef.current = sessionKey;
                  setState('ready');
                  if (onInitialTurnRef.current) {
                    onInitialTurnRef.current(firstTurn);
                  }
                  return;
                }
                initializedAttemptRef.current = sessionKey;
                setState('ready');
                return;
              }
            }
            throw new Error(res.error?.message || 'Failed to resume simulation attempt.');
          }
        } else if (scenarioId) {
          setState('starting');
          setInitProgressStage('starting');

          // Concurrently fetch briefing and start attempt in parallel!
          const [briefingRes, startRes] = await Promise.all([
            simulationService.getBriefing(scenarioId).catch(() => ({ success: false, data: null })),
            withTimeout(
              simulationService.startAttempt(scenarioId, language),
              ATTEMPT_INIT_TIMEOUT_MS,
              'Dialogue engine start timed out.'
            )
          ]);
          if (cancelToken.isCancelled) return;

          if (briefingRes?.success && briefingRes?.data) {
            setBriefing(briefingRes.data);
          }

          if (startRes.success && startRes.data) {
            const newAttemptId = startRes.data.attemptId || (startRes.data as any).id;
            setAttemptId(newAttemptId);

            if ((startRes.data as any).videoAnalysisEnabled !== undefined) {
              setVideoAnalysisEnabled(Boolean((startRes.data as any).videoAnalysisEnabled));
            } else if ((startRes.data as any).attemptSnapshot?.videoAnalysisEnabled !== undefined) {
              setVideoAnalysisEnabled(Boolean((startRes.data as any).attemptSnapshot?.videoAnalysisEnabled));
            }
            if ((startRes.data as any).videoAnalysisConfig) {
              setVideoAnalysisConfig((startRes.data as any).videoAnalysisConfig);
            } else if ((startRes.data as any).attemptSnapshot?.videoAnalysisConfig) {
              setVideoAnalysisConfig((startRes.data as any).attemptSnapshot?.videoAnalysisConfig);
            }
            if ((startRes.data as any).effectiveTTSProvider) {
              setEffectiveTTSProvider((startRes.data as any).effectiveTTSProvider);
            } else if ((startRes.data as any).attemptSnapshot?.effectiveTTSProvider) {
              setEffectiveTTSProvider((startRes.data as any).attemptSnapshot?.effectiveTTSProvider);
            }

            // Attempt startup is the authority for voice, engine and gender.
            const startedVoice = resolveEffectiveVoiceFields(
              [[(startRes.data as any).initialTurn, startRes.data, (startRes.data as any).attemptSnapshot]],
              {}
            );
            if (startedVoice.isBackendAuthoritative) {
              lockVoiceContract(startedVoice);
            }

            const initialText =
              startRes.data.initialTurn?.text ||
              (startRes.data as any).openingUtterance ||
              (startRes.data as any).openingStatement;
            if (initialText) {
              const audioUrl =
                startRes.data.initialTurn?.audioUrl ||
                ((startRes.data as any).openingAudioBase64
                  ? `data:${(startRes.data as any).openingMimeType || 'audio/mpeg'};base64,${(startRes.data as any).openingAudioBase64}`
                  : undefined);
              const initialPersonaName =
                (startRes.data as any)?.initialTurn?.personaName ||
                (startRes.data as any)?.personaName ||
                (startRes.data as any)?.scenarioConfig?.personas?.[0]?.name ||
                (briefingRes?.data as any)?.personas?.[0]?.name ||
                (briefingRes?.data as any)?.simulation?.personas?.[0]?.name ||
                'Customer';
              const localVoice = resolvePersonaVoice(
                initialPersonaName,
                undefined,
                briefingRes?.data || (startRes.data as any)?.scenarioConfig || briefing
              );
              // Backend-resolved values win; local resolution only covers legacy responses.
              const voiceConfig = voiceContractRef.current || localVoice;
              const firstTurn: Turn = {
                speaker: 'PERSONA',
                personaName: initialPersonaName,
                speakerName: initialPersonaName,
                text: initialText,
                timestamp: Date.now(),
                audioUrl,
                ttsVoice: voiceConfig.ttsVoice,
                ttsProvider: voiceConfig.ttsProvider as Turn['ttsProvider'],
                country: voiceConfig.country || localVoice.country,
                tone: localVoice.tone,
                speakingPace: localVoice.speakingPace,
                pitchPreference: localVoice.pitchPreference,
                genderPreference: voiceConfig.genderPreference || localVoice.genderPreference,
              };
              setTurns([firstTurn]);
              initializedAttemptRef.current = sessionKey;
              setState('ready');
              if (onInitialTurnRef.current) {
                onInitialTurnRef.current(firstTurn);
              }
            } else {
              initializedAttemptRef.current = sessionKey;
              setState('ready');
            }
          } else if (
            startRes.error?.code === 'ACTIVE_ATTEMPT_EXISTS' ||
            (startRes as any).code === 'ACTIVE_ATTEMPT_EXISTS' ||
            (startRes.error as any)?.attemptId ||
            (startRes as any)?.attemptId ||
            (startRes as any)?.activeAttempt?.id
          ) {
            // Gracefully resume existing active attempt
            const existingAttemptId =
              (startRes.error as any)?.attemptId ||
              (startRes as any)?.attemptId ||
              (startRes as any)?.activeAttempt?.id;

            if (existingAttemptId) {
              const resumeRes = await withTimeout(
                simulationService.resumeAttempt(existingAttemptId),
                ATTEMPT_INIT_TIMEOUT_MS,
                'Active attempt resume timed out.'
              );
              if (!cancelToken.isCancelled && resumeRes.success && resumeRes.data) {
                setAttemptId(existingAttemptId);

                if ((resumeRes.data as any).effectiveTTSProvider) {
                  setEffectiveTTSProvider((resumeRes.data as any).effectiveTTSProvider);
                } else if ((resumeRes.data as any).attemptSnapshot?.effectiveTTSProvider) {
                  setEffectiveTTSProvider((resumeRes.data as any).attemptSnapshot?.effectiveTTSProvider);
                }
                const resumedVoice = resolveEffectiveVoiceFields(
                  [[resumeRes.data, (resumeRes.data as any).attemptSnapshot]],
                  {}
                );
                if (resumedVoice.isBackendAuthoritative) {
                  lockVoiceContract(resumedVoice);
                }

                const rawTurns =
                  resumeRes.data.turns ||
                  (resumeRes.data as any).attempt?.turns ||
                  (resumeRes.data as any).state?.autosaveData?.turns ||
                  [];
                const fallbackConfig =
                  resumeRes.data.scenarioConfig ||
                  (resumeRes.data as any).scenario ||
                  (resumeRes.data as any).attemptSnapshot?.scenario ||
                  briefing;
                const resumeFallback = resolvePersonaVoice(
                  (resumeRes.data as any).personaName || (fallbackConfig as any)?.personas?.[0]?.name,
                  undefined,
                  fallbackConfig
                );
                let normalizedTurns: Turn[] = (Array.isArray(rawTurns) ? rawTurns : []).map((t: any) => {
                  const speakerName = (
                    (t.speaker || t.role || '').toUpperCase() === 'LEARNER' ||
                    (t.speaker || t.role || '').toUpperCase() === 'USER'
                      ? 'LEARNER'
                      : (t.speaker || t.role || '').toUpperCase() === 'COACH'
                      ? 'COACH'
                      : 'PERSONA'
                  ) as any;
                  const isPersonaTurn = speakerName === 'PERSONA' || speakerName === 'COACH';
                  const turnVoice = isPersonaTurn
                    ? resolveEffectiveVoiceFields([[t], [voiceContractRef.current]], resumeFallback)
                    : null;
                  return {
                    speaker: speakerName,
                    personaName: t.personaName,
                    speakerName: t.speakerName,
                    text: t.text || t.content || t.message || '',
                    timestamp: typeof t.timestamp === 'number' ? t.timestamp : (t.timestamp ? new Date(t.timestamp).getTime() : Date.now()),
                    audioUrl: t.audioUrl || (t.audioBase64 ? `data:${t.audioMimeType || 'audio/mpeg'};base64,${t.audioBase64}` : undefined),
                    ttsVoice: turnVoice?.ttsVoice ?? t.ttsVoice,
                    ttsProvider: (turnVoice?.ttsProvider ?? t.ttsProvider) as Turn['ttsProvider'],
                    genderPreference: turnVoice?.genderPreference ?? t.genderPreference,
                    country: turnVoice?.country ?? t.country,
                    tone: t.tone,
                    speakingPace: t.speakingPace,
                    pitchPreference: t.pitchPreference,
                    stageIndex: typeof t.stageIndex === 'number' && !isNaN(t.stageIndex) ? t.stageIndex : (typeof t.dialogueState?.stageIndex === 'number' && !isNaN(t.dialogueState.stageIndex) ? t.dialogueState.stageIndex : undefined),
                    stageName: t.stageName || t.dialogueState?.stageName,
                  };
                });

                // Fallback to opening utterance if no turns recorded yet
                const opening =
                  (resumeRes.data as any).openingUtterance ||
                  (resumeRes.data as any).openingStatement ||
                  (resumeRes.data as any).scenario?.persona?.openingStatement ||
                  (resumeRes.data as any).attemptSnapshot?.scenario?.openingStatement ||
                  (resumeRes.data.scenarioConfig as any)?.openingStatement ||
                  (resumeRes.data as any).attemptSnapshot?.scenario?.personas?.[0]?.openingStatement;

                const openingAudio = (resumeRes.data as any).openingAudioBase64
                  ? `data:${(resumeRes.data as any).openingMimeType || 'audio/mpeg'};base64,${(resumeRes.data as any).openingAudioBase64}`
                  : undefined;
                const personaNameFromConfig =
                  (resumeRes.data as any).scenarioConfig?.personas?.[0]?.name ||
                  (resumeRes.data as any).attemptSnapshot?.scenario?.personas?.[0]?.name ||
                  (resumeRes.data as any).scenario?.persona?.name ||
                  'Customer';

                if (normalizedTurns.length === 0 && opening) {
                  normalizedTurns = [
                    {
                      speaker: 'PERSONA',
                      personaName: personaNameFromConfig,
                      speakerName: personaNameFromConfig,
                      text: opening,
                      timestamp: Date.now(),
                      audioUrl: openingAudio,
                      ttsVoice: (voiceContractRef.current?.ttsVoice ?? resumeFallback.ttsVoice),
                      ttsProvider: (voiceContractRef.current?.ttsProvider ?? resumeFallback.ttsProvider) as Turn['ttsProvider'],
                      genderPreference: voiceContractRef.current?.genderPreference ?? resumeFallback.genderPreference,
                      country: voiceContractRef.current?.country || resumeFallback.country,
                      tone: resumeFallback.tone,
                      speakingPace: resumeFallback.speakingPace,
                      pitchPreference: resumeFallback.pitchPreference,
                    },
                  ];
                } else if (normalizedTurns.length > 0 && normalizedTurns[0].speaker === 'PERSONA') {
                  if (!normalizedTurns[0].audioUrl && openingAudio) {
                    normalizedTurns[0].audioUrl = openingAudio;
                  }
                  if (!normalizedTurns[0].personaName) {
                    normalizedTurns[0].personaName = personaNameFromConfig;
                    normalizedTurns[0].speakerName = personaNameFromConfig;
                  }
                }

                setTurns(normalizedTurns);
            const resumedStageTurn = [...normalizedTurns].reverse().find((t: any) => typeof t.stageIndex === 'number' && !isNaN(t.stageIndex));
            const resumedStageIdx = resumedStageTurn?.stageIndex ?? (resumeRes.data as any).currentStageIndex ?? (resumeRes.data as any).attemptSnapshot?.currentStageIndex;
            if (typeof resumedStageIdx === 'number' && !isNaN(resumedStageIdx)) {
              setCurrentStageIndex(resumedStageIdx);
            }
                const scenarioConfig = fallbackConfig;
                if (scenarioConfig) {
                  setBriefing(scenarioConfig);
                }

                initializedAttemptRef.current = sessionKey;
                setState('ready');
                if (normalizedTurns.length === 1 && normalizedTurns[0].speaker === 'PERSONA' && onInitialTurnRef.current) {
                  onInitialTurnRef.current(normalizedTurns[0]);
                }
                return;
              }
            }
            throw new Error(startRes.error?.message || 'An active attempt already exists. Please resume it.');
          } else {
            throw new Error(startRes.error?.message || 'Failed to start simulation attempt.');
          }
        }
      } catch (err: any) {
        if (cancelToken.isCancelled) return;
        throw err;
      }
    }

    async function executeWithRetry(cancelToken: { isCancelled: boolean }) {
      try {
        await initSession(cancelToken);
      } catch (err: any) {
        if (cancelToken.isCancelled) return;
        console.warn('[useSimulationLifecycle] Init attempt 1 failed, retrying after 800ms backoff...', err?.message);
        await new Promise((r) => setTimeout(r, 800));
        if (cancelToken.isCancelled) return;
        try {
          await initSession(cancelToken);
        } catch (retryErr: any) {
          if (cancelToken.isCancelled) return;
          isInitializingRef.current = false;
          initializedAttemptRef.current = null;
          console.error('Simulation lifecycle init failed after retry:', retryErr);
          setError(retryErr?.message || 'Simulation initialization failed. Click Reconnect to try again.');
          setInitProgressStage('error');
          setState('error');
          if (onErrorRef.current) onErrorRef.current(retryErr);
        }
      } finally {
        isInitializingRef.current = false;
        setIsReconnecting(false);
      }
    }

    const cancelToken = { isCancelled: false };
    activeCancelRef.current = cancelToken;
    executeWithRetry(cancelToken);

    return () => {
      cancelToken.isCancelled = true;
      isInitializingRef.current = false;
      initializedAttemptRef.current = null;
    };
  }, [scenarioId, initialAttemptId, language, enabled, lockVoiceContract]);

  const reconnect = useCallback(async () => {
    activeCancelRef.current.isCancelled = true;
    initializedAttemptRef.current = null;
    isInitializingRef.current = true;
    setError(null);
    setState('initializing');
    setIsReconnecting(true);
    setInitProgressStage('connecting');

    const newCancel = { isCancelled: false };
    activeCancelRef.current = newCancel;
    try {
      if (initialAttemptId) {
        const briefingPromise = scenarioId
          ? simulationService.getBriefing(scenarioId).catch(() => null)
          : Promise.resolve(null);

        const res = await withTimeout(
          simulationService.resumeAttempt(initialAttemptId),
          ATTEMPT_INIT_TIMEOUT_MS,
          'Attempt resume connection timed out.'
        );
        if (newCancel.isCancelled) return;
        if (res.success && res.data) {
          setAttemptId(initialAttemptId);

          if ((res.data as any).videoAnalysisEnabled !== undefined) {
            setVideoAnalysisEnabled(Boolean((res.data as any).videoAnalysisEnabled));
          } else if ((res.data as any).attemptSnapshot?.videoAnalysisEnabled !== undefined) {
            setVideoAnalysisEnabled(Boolean((res.data as any).attemptSnapshot?.videoAnalysisEnabled));
          }
          if ((res.data as any).videoAnalysisConfig) {
            setVideoAnalysisConfig((res.data as any).videoAnalysisConfig);
          } else if ((res.data as any).attemptSnapshot?.videoAnalysisConfig) {
            setVideoAnalysisConfig((res.data as any).attemptSnapshot?.videoAnalysisConfig);
          }
          if ((res.data as any).effectiveTTSProvider) {
            setEffectiveTTSProvider((res.data as any).effectiveTTSProvider);
          } else if ((res.data as any).attemptSnapshot?.effectiveTTSProvider) {
            setEffectiveTTSProvider((res.data as any).effectiveTTSProvider);
          }

          const resumedVoice = resolveEffectiveVoiceFields(
            [[res.data, (res.data as any).attemptSnapshot]],
            {}
          );
          if (resumedVoice.isBackendAuthoritative) {
            lockVoiceContract(resumedVoice);
          }

          const rawTurns =
            res.data.turns ||
            (res.data as any).state?.autosaveData?.turns ||
            (res.data as any).attemptSnapshot?.scenario?.turns ||
            [];
          const scenarioConfig = res.data.scenarioConfig || res.data.scenario || (res.data as any).attemptSnapshot?.scenario;
          const resumeFallback = resolvePersonaVoice(
            (res.data as any).personaName ||
              (scenarioConfig as any)?.personas?.[0]?.name ||
              (res.data as any).attemptSnapshot?.scenario?.personas?.[0]?.name,
            undefined,
            scenarioConfig || briefing
          );

          if (Array.isArray(rawTurns) && rawTurns.length > 0) {
            const normalizedTurns: Turn[] = rawTurns.map((t: any) => {
              const speakerName = (
                (t.speaker || t.role || '').toUpperCase() === 'LEARNER' ||
                (t.speaker || t.role || '').toUpperCase() === 'USER'
                  ? 'LEARNER'
                  : (t.speaker || t.role || '').toUpperCase() === 'COACH'
                  ? 'COACH'
                  : 'PERSONA'
              ) as any;
              const isPersonaTurn = speakerName === 'PERSONA' || speakerName === 'COACH';
              const turnVoice = isPersonaTurn
                ? resolveEffectiveVoiceFields([[t], [voiceContractRef.current]], resumeFallback)
                : null;
              return {
                speaker: speakerName,
                personaName: t.personaName,
                speakerName: t.speakerName,
                text: t.text || t.content || t.message || '',
                timestamp: typeof t.timestamp === 'number' ? t.timestamp : (t.timestamp ? new Date(t.timestamp).getTime() : Date.now()),
                audioUrl: t.audioUrl || (t.audioBase64 ? `data:${t.audioMimeType || 'audio/mpeg'};base64,${t.audioBase64}` : undefined),
                ttsVoice: turnVoice?.ttsVoice ?? t.ttsVoice,
                ttsProvider: (turnVoice?.ttsProvider ?? t.ttsProvider) as Turn['ttsProvider'],
                genderPreference: turnVoice?.genderPreference ?? t.genderPreference,
                country: turnVoice?.country ?? t.country,
                tone: t.tone,
                speakingPace: t.speakingPace,
                pitchPreference: t.pitchPreference,
                stageIndex: typeof t.stageIndex === 'number' && !isNaN(t.stageIndex) ? t.stageIndex : (typeof t.dialogueState?.stageIndex === 'number' && !isNaN(t.dialogueState.stageIndex) ? t.dialogueState.stageIndex : undefined),
                stageName: t.stageName || t.dialogueState?.stageName,
                llmDebug: t.llmDebug,
                latencyMetrics: t.latencyMetrics,
              };
            });
            setTurns(normalizedTurns);
            const resumedStageTurn = [...normalizedTurns].reverse().find((t: any) => typeof t.stageIndex === 'number' && !isNaN(t.stageIndex));
            const resumedStageIdx = resumedStageTurn?.stageIndex ?? (res.data as any).currentStageIndex ?? (res.data as any).attemptSnapshot?.currentStageIndex;
            if (typeof resumedStageIdx === 'number' && !isNaN(resumedStageIdx)) {
              setCurrentStageIndex(resumedStageIdx);
            }
          }

          if (scenarioConfig) {
            setBriefing({
              ...(scenarioConfig as any),
              startedAt: (res.data as any).startedAt || (scenarioConfig as any)?.startedAt,
              timeLimitMinutes: (res.data as any).timeLimitMinutes || (scenarioConfig as any)?.timeLimitMinutes,
              elapsedSeconds: (res.data as any).elapsedSeconds ?? (scenarioConfig as any)?.elapsedSeconds,
              timeRemainingSeconds: (res.data as any).timeRemainingSeconds ?? (scenarioConfig as any)?.timeRemainingSeconds,
            } as any);
          } else {
            briefingPromise.then((bRes) => {
              if (bRes?.success && bRes?.data && !newCancel.isCancelled) {
                setBriefing(bRes.data);
              }
            });
          }
          initializedAttemptRef.current = `attempt_${initialAttemptId}`;
          setInitProgressStage('ready');
          setState('ready');
          return;
        } else if (scenarioId) {
          // Fall back gracefully to starting fresh attempt if resumed ID is invalid/expired
          console.warn(`[reconnect] Resume failed for ${initialAttemptId}. Starting fresh attempt for ${scenarioId}.`);
          const [briefingRes, startRes] = await Promise.all([
            simulationService.getBriefing(scenarioId).catch(() => ({ success: false, data: null })),
            withTimeout(
              simulationService.startAttempt(scenarioId, language),
              ATTEMPT_INIT_TIMEOUT_MS,
              'Dialogue engine start timed out.'
            )
          ]);
          if (newCancel.isCancelled) return;
          if (briefingRes?.success && briefingRes?.data) setBriefing(briefingRes.data);
          if (startRes.success && startRes.data) {
            const newAttemptId = startRes.data.attemptId || (startRes.data as any).id;
            setAttemptId(newAttemptId);
            initializedAttemptRef.current = `scenario_${scenarioId}_${language}`;
            setInitProgressStage('ready');
            setState('ready');
            return;
          }
        }
      } else if (scenarioId) {
        const [briefingRes, startRes] = await Promise.all([
          simulationService.getBriefing(scenarioId).catch(() => ({ success: false, data: null })),
          withTimeout(
            simulationService.startAttempt(scenarioId, language),
            ATTEMPT_INIT_TIMEOUT_MS,
            'Dialogue engine start timed out.'
          )
        ]);
        if (newCancel.isCancelled) return;
        if (briefingRes?.success && briefingRes?.data) setBriefing(briefingRes.data);
        if (startRes.success && startRes.data) {
          const newAttemptId = startRes.data.attemptId || (startRes.data as any).id;
          setAttemptId(newAttemptId);
          initializedAttemptRef.current = `scenario_${scenarioId}_${language}`;
          setInitProgressStage('ready');
          setState('ready');
          return;
        }
      }
    } catch (err: any) {
      if (newCancel.isCancelled) return;
      setError(err?.message || 'Reconnection failed. Please check your network and try again.');
      setInitProgressStage('error');
      setState('error');
    } finally {
      isInitializingRef.current = false;
      setIsReconnecting(false);
    }
  }, [scenarioId, initialAttemptId, language, briefing, lockVoiceContract]);

  // Periodic Heartbeat to maintain active session lease
  useEffect(() => {
    if (!attemptId || !['ready', 'listening', 'speaking', 'processing'].includes(state)) {
      return;
    }

    const heartbeatInterval = setInterval(() => {
      simulationService.sendHeartbeat(attemptId).catch((hErr) => {
        console.debug('[useSimulationLifecycle] Heartbeat error:', hErr?.message);
      });
    }, 20_000); // Send heartbeat every 20 seconds (lease duration is 60s)

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [attemptId, state]);

  // Submit learner turn
  const submitTurn = useCallback(
    async (
      learnerUtterance: string,
      speechMeta?: import('./useSimulationAudio').LearnerTurnSpeechMeta
    ): Promise<boolean> => {
      const text = learnerUtterance.trim();
      if (!text || !attemptId || isSubmittingTurnRef.current) return false;

      isSubmittingTurnRef.current = true;
      setState('processing');

      const learnerTurn: Turn = {
        speaker: 'LEARNER',
        text,
        timestamp: speechMeta?.speechEndMs || Date.now(),
        stageIndex: currentStageIndex,
        speechMetrics: speechMeta,
        interruption: speechMeta?.interruption ?? null,
        interruptions: speechMeta?.interruptions,
      };

      setTurns((prev) => [...prev, learnerTurn]);

      try {
        const res = await simulationService.submitTurn(attemptId, text, speechMeta);
        if (res.success && res.data) {
          const payload = res.data as any;
          const aiTurn = payload.aiTurn || payload.data?.aiTurn;
          const replyText =
            aiTurn?.text ||
            payload.replyText ||
            payload.response ||
            payload.data?.replyText ||
            payload.data?.response;

          if (replyText) {
            let audioUrl: string | undefined = undefined;
            const audioBase64 =
              aiTurn?.audioBase64 ||
              payload.audioBase64 ||
              payload.data?.audioBase64 ||
              (aiTurn?.audioSegments && aiTurn.audioSegments[0]?.audioBase64);

            if (audioBase64) {
              audioUrl = audioBase64.startsWith('data:')
                ? audioBase64
                : `data:audio/mpeg;base64,${audioBase64}`;
            } else if (payload.audioUrl || payload.data?.audioUrl) {
              audioUrl = payload.audioUrl || payload.data?.audioUrl;
            }

            const conversationEnded = Boolean(
              payload.conversationEnded ||
              payload.isTerminal ||
              payload.data?.conversationEnded ||
              payload.data?.isTerminal ||
              aiTurn?.conversationEnded ||
              aiTurn?.isTerminal
            );

            const effectivePersonaName =
              aiTurn?.personaName ||
              (aiTurn?.speakerName ? aiTurn.speakerName.split('(')[0].trim() : undefined) ||
              payload.personaName;
            const effectivePersonaId = aiTurn?.personaId || payload.personaId;
            const localVoice = resolvePersonaVoice(
              effectivePersonaName,
              effectivePersonaId,
              briefing || payload.scenarioConfig || payload.data?.scenarioConfig
            );
            // This turn's contract first, then the one locked at attempt startup, and
            // only then local resolution. Each source is adopted whole so a missing
            // field can never pair a backend voice with a different engine.
            const voiceConfig = resolveEffectiveVoiceFields(
              [[aiTurn, payload, payload.data], voiceContractRef.current],
              localVoice
            );
            if (voiceConfig.isBackendAuthoritative) {
              lockVoiceContract(voiceConfig);
            }

            const personaTurn: Turn = {
              speaker: 'PERSONA',
              text: replyText,
              timestamp: Date.now(),
              audioUrl,
              personaName: effectivePersonaName,
              personaId: effectivePersonaId,
              speakerName: aiTurn?.speakerName || payload.speakerName,
              ttsVoice: voiceConfig.ttsVoice,
              ttsProvider: voiceConfig.ttsProvider as Turn['ttsProvider'],
              country: voiceConfig.country || localVoice.country,
              tone: aiTurn?.tone || localVoice.tone,
              speakingPace: aiTurn?.speakingPace || localVoice.speakingPace,
              pitchPreference: aiTurn?.pitchPreference || localVoice.pitchPreference,
              genderPreference: voiceConfig.genderPreference || localVoice.genderPreference,
              stageIndex: payload.currentStageIndex ?? payload.stageIndex,
              stageName: payload.stageName,
              conversationEnded,
              isTerminal: conversationEnded,
            };
            setTurns((prev) => [...prev, personaTurn]);
            const nextStageIndex = payload.currentStageIndex ?? payload.stageIndex ?? (aiTurn as any)?.stageIndex;
            if (typeof nextStageIndex === 'number' && !isNaN(nextStageIndex)) {
              setCurrentStageIndex(nextStageIndex);
            }
            if (onAiResponseRef.current && stateRef.current !== 'completing' && stateRef.current !== 'completed') {
              onAiResponseRef.current(personaTurn);
            }

            // Let AI speak the complete sentence; completeAttempt will transition to completing
            if (stateRef.current !== 'completing' && stateRef.current !== 'completed') {
              setState('listening');
            }
          } else {
            setState('listening');
          }
          return true;
        } else {
          throw new Error(res.error?.message || 'Failed to process learner turn.');
        }
      } catch (err: any) {
        console.error('Turn submission error:', err);
        setError(err.message || 'Turn exchange failed.');
        if (onError) onError(err);
        setState('ready');
        return false;
      } finally {
        isSubmittingTurnRef.current = false;
      }
    },
    [attemptId, briefing, onError, lockVoiceContract]
  );

  // Complete / Conclude attempt
  const completeAttempt = useCallback(
    async (reason = 'LEARNER_CONFIRMED', clientTurns?: Turn[], elapsedSeconds?: number) => {
      // Re-entrancy guard: timer expiry, terminal AI turn, and manual "End"
      // can all fire in the same tick — the attempt must be finalized once.
      if (!attemptId || isCompletingRef.current) return null;
      isCompletingRef.current = true;
      setState('completing');

      try {
        const effectiveTurns = clientTurns && clientTurns.length > 0 ? clientTurns : turns;
        const res = await simulationService.completeAttempt(attemptId, reason, effectiveTurns, elapsedSeconds);
        setState('completed');
        if (onComplete) onComplete();
        return res;
      } catch (err: any) {
        console.error('Complete attempt error:', err);
        setError(err.message || 'Failed to evaluate simulation.');
        if (onError) onError(err);
        setState('completed');
        if (onComplete) onComplete();
        return null;
      }
    },
    [attemptId, turns, onComplete, onError]
  );

  return {
    attemptId,
    state,
    setState,
    briefing,
    currentStageIndex,
    turns,
    setTurns,
    error,
    submitTurn,
    completeAttempt,
    videoAnalysisEnabled,
    videoAnalysisConfig,
    effectiveTTSProvider,
    voiceContract,
    isReconnecting,
    initProgressStage,
    reconnect,
  };
}
